<template>
  <div class="container">
    <div class="briefs-overview">
      <div class="common-table briefs-overview__table" v-if="isDataFetched">
        <div class="common-table__row common-table__header">
          <div v-for="(column) in columnsConfig"
               class="common-table__header-cell"
               :class="{[`briefs-overview__table-${column.property}`]: true}"
               :key="column.property"
               @click="onHeaderClicked($event, column)">
            <base-multi-select-sortable-header
              v-if="column.isSortable"
              :column="column"
              :sort-config="sortConfig"
              @sort="onHeaderClicked(column, $event)"
              @apply="filterColumn($event, column)"
              @cancel="cancelFilter($event, column)"
            />
          </div>
        </div>
        <div v-if="filteredBriefsList.length"
             key="table">
          <div class="common-table__row"
               v-for="brief in filteredBriefsList"
               :key="brief._id">
            <div v-for="(column, index) in columnsConfig"
                 :key="index"
                 class="common-table__cell"
                 :class="`briefs-overview__table-${column.property}`">
              <div class="briefs-overview__table-cell-wrapper" :class="{'briefs-overview__table-link': column.property === 'url'}">
                <template v-if="column.internalLink">
                  <router-link :to="`/content-briefs/${brief._id}`"
                               class="briefs-overview__brief-name"
                                :class="{'briefs-overview__brief-name_disabled': isContentBriefDisabled}">
                    {{brief[column.property]}}
                  </router-link>
                </template>
                <template v-else-if="column.property === 'url_status'">
                  <base-select
                    class="briefs-overview__select"
                    :options="column.allowedOptions"
                    border-less
                    alignLeft
                    :headerStyle="{}"
                    :disabled="urlIsPublished(brief) || isCurrentUserClientCollaborator"
                    :titleStyle="selectStyle"
                    showTopBorder
                    shiftLabelRight
                    v-model="brief.currentUrlStatus"
                    :selectStyle="{'margin-left': '-15px', height: '60px!important'}"
                    @change="openModal(brief)"/>
                </template>
                <template v-else-if="column.outerLink">
                  <a class="briefs-overview__link" :href="brief[column.property]" target="_blank">
                       <base-truncate-label
                    :label="brief[column.property]"
                    watchClass="briefs-overview__table-link"/>
                    </a>
                </template>
                <template v-else-if="column.property === 'target_date'"
                          :class="{'briefs-overview__date_wrapper-narrow': !brief[column.property]}">
                  <div class="briefs-overview__date_wrapper">
                    <v-date-picker class="briefs-overview__date"
                                   :ref="brief._id"
                                   v-if="!isCurrentUserClientCollaborator"
                                   :popover="{visibility: pickerState[brief._id]}"
                                   @input="selectNewDate($event, brief)"
                                   :input-props="{
                                    class: 'hidden',
                                    readonly: true
                                  }">
                    </v-date-picker>

                    <button class="briefs-overview__date-button" @click="openPicker($event, brief)"
                            :class="{'briefs-overview__date-button_highlighted': pickerState[brief._id] === 'visible',
                            'briefs-overview__date-button_disabled': isCurrentUserClientCollaborator}"
                            v-if="brief[column.property]">
                      {{brief[column.property] | formattedDate}}
                    </button>
                    <base-material-design-icon icon="calendar"
                                               class="briefs-overview__date-button briefs-overview__date-button_icon"
                                               @click="openPicker($event, brief)"
                                               :class="{'briefs-overview__date-button_highlighted': pickerState[brief._id] === 'visible',
                                               'briefs-overview__date-button_disabled': isCurrentUserClientCollaborator}"
                                               v-if="!brief[column.property]">
                    </base-material-design-icon>
                  </div>
                </template>
                <template v-else>{{getCellValue(brief, column.property)}}</template>
              </div>
            </div>
          </div>
        </div>
        <div v-else
             key="not-found"
             class="briefs-overview__not-found">
          Awaiting Data
        </div>
      </div>
      <div v-else
           key="loader"
           class="briefs-overview__loader">
        <base-loader/>
      </div>
      <div v-if="isDataUpdating" class="briefs-overview__update-loader">
        <base-loader class="briefs-overview__update-loader-ring"/>
      </div>
    </div>
    <modal name="status"
           v-bind="changeStatusModalProps"
    >
      <UrlStatusPopup
        :url="changingStatusData"
        :statuses="urlStatuses"
        :isContentBrief="true"
        @apply="statusApplied"
        @cancel="statusCancel"/>
    </modal>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import _ from 'lodash'
import moment from 'moment'
import { ALLOWED_URL_STATUS_NAMES, types } from '@/store/modules/content-briefs/briefsOverviewModule'
import helper from '@/services/Helper'
import baseModalProps from '@/common/BaseModalProps'
import UrlStatusPopup from '../url-planning/UrlStatusPopup'
import filters from '@/common/Filters'
import ToasterService from '@/services/toasterService'

const EMPTY_FILTERS = {
  section: [],
  url_status: [],
  url: null,
  name: null,
  date_from: null,
  date_to: null
}
/**
 * List of client's briefs
 */
export default {
  name: 'BriefsOveriview',
  data () {
    return {
      isDataFetched: false,
      isDataUpdating: false,
      isDatePickerOpened: false,
      sortConfig: [],
      sortedBriefsList: [],
      changingStatusData: {},
      filteredBriefsList: [],
      pickerState: {},
      filterParams: { ...EMPTY_FILTERS },
      // table config
      columnsConfig: [
        {
          label: 'Brief Name',
          property: 'name',
          internalLink: true, // link to the content brief
          isSortable: true,   // if this column is sortable
          isDefaultSort: true // sorted by default by load the page
        },
        {
          label: 'URL Status',
          property: 'url_status',
          isSortable: true,
          options: [],        // options for BaseMultiSelectSortableHeader#BaseNumberFilterDropdown
          allowedOptions: [], // options for BaseSelect dropdown in table row. We use it to change url status from table
          valuePreFormatter: (status) => { // sort preformatter
            if (!status) {
              return null
            }
            if(!status.name){
              return ''
            }
            return status.name
          }
        },
        {
          label: 'Section',
          property: 'section',
          isSmartInput: true, // property for BaseMultiSelectSortableHeader
          isSortable: true,
          options: [],
          valuePreFormatter: (section) => {
            if (!section) {
              return null
            }
            if(!section.name){
              return ''
            }
            return section.name
          }
        },
        {
          label: 'Target Date',
          property: 'target_date',
          isSortable: true,
          valuePreFormatter: (timestamp) => {
            if (!timestamp) {
              return null
            }
            return moment(timestamp).valueOf()
          }
        },
        {
          label: 'URL',
          property: 'url',
          outerLink: true, // property means content in the column acts as url
          isSortable: true
        }
      ]
    }
  },
  components: {
    UrlStatusPopup
  },
  async created () {
    if (this.selectedClient) {
      this.getData()
    }
    document.addEventListener('click', this.clickHandler)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.clickHandler)
    this.resetPage()
  },
  computed: {
    ...mapState({
      urlStatuses: 'urlStatuses',
      briefsList: state => state.briefsOverview.briefsList,
      briefStatuses: state => state.briefsOverview.filteredStatuses,
      briefSections: state => state.briefsOverview.filteredSections,
      activeStatus: state => state.briefsOverview.activeStatus,
      activeSection: state => state.briefsOverview.activeSection
    }),
    selectStyle () {
      return this.isCurrentUserClientCollaborator
        ? { 'padding-left': '5px', color: '#7a8894' }
        : { 'padding-left': '5px' }
    },
    changeStatusModalProps: function () {
      const changeStatusModalProps = {
        width: 690
      }
      return { ...baseModalProps, ...changeStatusModalProps }
    }
  },
  watch: {
    selectedClient: async function (client) {
      if (!client) {
        return
      }
      this.isDataFetched = false
      this.resetPage()
      this.getData()
    },
    briefsList: function () {
      this.pickerState = {}
      this.briefsList.forEach(item => {
        // we need to save two url status states to reset it if changing logic would be canceled
        item.currentUrlStatus = item['url_status']._id
        item.previousUrlStatus = item['url_status']._id
        // Vue reactivity stuff
        this.$set(this.pickerState, item._id, 'focus')
      })
      this.sortedBriefsList = []
      this.sortedBriefsList.push(..._.cloneDeep(this.briefsList))
      this.updateSortedBriefsList()
    },
    briefStatuses (value) {
      if (!value || !value.length) {
        return []
      }
      const statuses = value.map(helper.convertListToOptions)
      // add converted list to table config options
      const statusesConfig = this.columnsConfig.find(item => item.property === 'url_status')
      statusesConfig.options = statuses
    },
    briefSections (value) {
      if (!value || !value.length) {
        return []
      }
      const sections = value.map(item => {
        return { value: item, label: item }
      })
      // add converted list to table config options
      const sectionsConfig = this.columnsConfig.find(item => item.property === 'section')
      sectionsConfig.options = sections
    },
    urlStatuses: {
      handler (value) {
        if (!value || !value.length) {
          return []
        }
        const statuses = value.filter(item => ALLOWED_URL_STATUS_NAMES.includes(item.label.toLowerCase()))
          .map(helper.convertListToOptions)
        // add converted list to table config options
        const statusesConfig = this.columnsConfig.find(item => item.property === 'url_status')
        statusesConfig.allowedOptions = statuses
      },
      immediate: true
    },
    /**
     * @alias filterParamsWatcher
     * */
    filterParams: {
      handler () {
        this.updateSortedBriefsList()
      },
      deep: true
    },
    isContentBriefDisabled (brief) {
      return brief.status === 'Draft' && !this.isCurrentUserAgent
    }
  },
  methods: {
    ...mapActions([
      'getBriefOverviewData', 'resetData'
    ]),
    ...mapActions('briefContent', [
      'changeContentBrief'
    ]),
    /**
     * get page data
     */
    async getData () {
      await this.getBriefOverviewData(this.clientId)
      this.isDataFetched = true
    },
    /**
     * callback to close manually opened date picker
     * @param event
     */
    clickHandler (event) {
      if (!this.isDatePickerOpened) {
        return
      }
      const classList = Array.from(_.get(event, 'target.classList', []))
      const insideDatePicker = classList.some(className => className.startsWith('vc-'))
      if (insideDatePicker) {
        return
      }
      this.resetPickerState()
      this.isDatePickerOpened = false
    },
    urlIsPublished: function (contentBrief) {
      const statusName = contentBrief.url_status.name ? contentBrief.url_status.name.toLowerCase() : ''
      return statusName === 'published'
    },
    getCellValue: function (brief, columnProperty) {
      const value = brief[columnProperty]
      if (typeof value === 'object') {
        return value.name
      }
      return value
    },
    /**
     * update filterParam
     * filter logic triggers from filterColumn watcher {@link filterParamsWatcher}
     * @param event data from BaseMultiSelectSortableHeader
     * @param column - column config
     */
    filterColumn: function (event, column) {
      if (column.property === 'target_date') {
        this.filterParams.date_from = event.date_from
        this.filterParams.date_to = event.date_to
      } else {
        this.filterParams[column.property] = event
      }
    },
    /**
     * clear specific filter
     * */
    cancelFilter (event, column) {
      if (column.property === 'target_date') {
        this.filterParams.date_from = null
        this.filterParams.date_to = null
      } else {
        this.filterParams[column.property] = null
      }
    },
    /**
     * start changing status logic
     * we need to save changing brief to reset it if changing logic would be canceled
     * */
    openModal (brief) {
      this.changingStatusData = brief
      this.$modal.show('status')
    },
    statusCancel () {
      this.changingStatusData.currentUrlStatus = this.changingStatusData.previousUrlStatus
      this.$set(this.filteredBriefsList, this.filteredBriefsList.findIndex(item => item._id === this.changingStatusData._id), this.changingStatusData)
      this.$modal.hide('status')
    },
    clearDateListener () {
      this.updateContentBrief({ date: 'clear', briefId: this.changingStatusData._id })
    },
    resetPage () {
      this.resetData()
      this.sortConfig = []
      this.filterParams = { ...EMPTY_FILTERS }
    },
    /**
     * callback of sort buttons
     * */
    onHeaderClicked (column, direction) {
      column.isSortable && this.changeSorting(direction, column)
    },
    /**
     * sort logic
     * */
    changeSorting (direction, columnConfig) {
      const propName = columnConfig.property
      const currentConfigIndex = this.sortConfig.findIndex(config => {
        return config.propertyName === propName
      })

      if ((!this.sortConfig.length || currentConfigIndex < 0) && direction !== 'clear') {
        const newSortConfig = getNewSortConfig(columnConfig, direction)
        this.sortConfig = [...this.sortConfig, newSortConfig]
        this.updateSortedBriefsList()
        return
      }
      if (direction === 'clear') {
        this.sortConfig.splice(currentConfigIndex, 1)
      } else {
        this.sortConfig[currentConfigIndex].sortDirection = direction
        this.sortConfig = [...this.sortConfig]
      }
      this.updateSortedBriefsList()
    },
    /**
     * sorting source brief list
     * start executing frontend sort-filter logic
     */
    updateSortedBriefsList () {
      const sortedList = this.sortConfig.length ? helper.sortByMultipleParams(this.briefsList, this.sortConfig) : this.briefsList
      this.sortedBriefsList = []
      this.sortedBriefsList.push(...sortedList)
      this.updateFilteredBriefList()
    },
    /**
     * filter sorted brief list
     */
    updateFilteredBriefList () {
      this.filteredBriefsList = []
      this.filteredBriefsList.push(...this.sortedBriefsList)
      if (this.isFilteredParams()) {
        Object.entries(this.filterParams).forEach(([key, value]) => {
          if (this.isNotEmptyFilter(value)) {
            if (value instanceof Array) {
              if (key === 'url_status') {
                this.filteredBriefsList = this.filteredBriefsList.filter(item => value.includes(item[key]._id))
              } else {
                this.filteredBriefsList = this.filteredBriefsList.filter(item => item[key].name && value.includes(item[key].name))
              }
            } else if (['url', 'name'].includes(key)) {
              this.filteredBriefsList = this.filteredBriefsList.filter(item => item[key].includes(value))
            } else if (key === 'date_from') {
              this.filteredBriefsList = this.filteredBriefsList.filter(item => {
                let targetDate = moment(item['target_date'])
                if (!targetDate || !targetDate.isValid()) {
                  return false
                }
                return targetDate.isBetween(this.filterParams['date_from'], this.filterParams['date_to'])
              })
            }
          }
        })
      }
    },
    isFilteredParams () {
      return Object.values(this.filterParams).some(item => this.isNotEmptyFilter(item))
    },
    isNotEmptyFilter (filter) {
      if (filter instanceof Array) {
        return !_.isEmpty(filter)
      }
      return Boolean(filter)
    },
    /**
     * close all opened date picker
     */
    resetPickerState: function () {
      Object.keys(this.pickerState).forEach(key => {
        this.pickerState[key] = 'focus'
      })
    },
    /**
     * open date picker mannualy
     * @param brief clicked brief
     */
    openPicker (event, brief) {
      this.changingStatusData = brief
      if (this.isCurrentUserClientCollaborator) {
        ToasterService.showToast(ToasterService.ERROR_TYPE, 'Forbidden. You are not allowed to change the date')
        return
      }
      this.resetPickerState()
      let handler = () => {
        this.pickerState[brief._id] = 'visible'
        this.isDatePickerOpened = true
        this.$nextTick(this.addClearButton)
      }
      handler = handler.bind(this)
      setTimeout(handler, 100)
    },
    async statusApplied ({ description, date }) {
      this.$modal.hide('status')
      if (moment(date).isValid()) {
        date = moment(date).format('YYYY-MM-DD')
      } else {
        date = null
      }
      this.updateContentBrief({
        briefId: this.changingStatusData._id,
        urlStatusId: this.changingStatusData.currentUrlStatus,
        description,
        date
      })
    },
    /**
     * callback for date picker
     * @param date - selected date
     * @param brief - brief object
     */
    selectNewDate (date, brief) {
      this.resetPickerState()
      this.changingStatusData = brief
      this.updateContentBrief({ date, briefId: brief._id })
    },
    /**
     * update content brief from url popup or date picker
     * @param data - content brief data
     */
    async updateContentBrief (data) {
      if (data) {
        let { urlStatusId, date, calendarId, description, briefId } = data
        const payload = {
          clientId: this.clientId,
          briefId: briefId
        }
        urlStatusId && (payload.urlStatusId = urlStatusId)
        description && (payload.description = description)
        if (date && moment(date).isValid()) {
          date = moment(date).format('YYYY-MM-DD')
          payload.date = date
          payload.calendarId = calendarId
        } else if (date === 'clear') {
          payload.date = date
        }
        try {
          this.isDataUpdating = true
          await this.changeContentBrief(payload)
          await this.getBriefOverviewData(this.clientId)
        } catch (e) {
        } finally {
          this.isDataUpdating = false
        }
      }
    }
  },
  filters: {
    ...filters
  }
}

function getNewSortConfig (columnConfig, direction = helper.DIRECTION_ASC) {
  return {
    propertyName: columnConfig.property,
    sortDirection: direction,
    valuePreFormatter: columnConfig.valuePreFormatter || null
  }
}
</script>

<style scoped lang="scss">

  .pull-top {
    margin-top: -22px;
  }

  .briefs-overview {
    padding-bottom: $rfx-bottom-padding;
    position: relative;

    &__link {
      text-decoration: none;
      color: $rfx-bright-blue;
      @include ellipsis;
    }

    &__select {
      /*cursor: pointer;*/
      &_selected {
        border-bottom: 2px solid $rfx-bright-blue !important;
      }

      &_opened {
        background-color: rgba(40, 74, 255, 0.08);;
        border-bottom: 2px solid $rfx-bright-blue;
      }
    }

    &__table {
      &-name {
        flex: 1 1 45%;
        color: $rfx-dark-march;
        font-weight: 500;
        overflow: hidden;
      }

      &-target_date {
        flex: 0 0 140px;
        position: relative;
        color: $rfx-light-march;
        max-width: 140px;

      }

      &-url_status {
        color: #011627;
        font-weight: 500;
        flex: 0 0 150px;
        position: relative;
      }

      &-section {
        flex: 0 0 130px;
        position: relative;
        color: $rfx-light-march;
        overflow: hidden;
      }

      &-url {
        flex: 1 1 55%;
        overflow: hidden;
      }

      &-link {
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1 1 55%;

        a {
          white-space: nowrap;
        }
      }

      &-cell-wrapper {
        display: flex;
        align-items: center;
        padding-left: 15px;
        height: 100%;
      }
    }

    &__clickable-header {
      position: relative;
      white-space: nowrap;
      cursor: pointer;

      &::after {
        content: '';
        color: inherit;
        display: inline-block;
        width: 7px;
        height: 7px;
        border-left: 2px solid currentColor;
        border-bottom: 2px solid currentColor;
        transform: translate(10px, -2px) rotate(-45deg);
      }
    }

    &__filter {
      width: 180px;
      left: -30px;
      top: 40px;
    }

    &__brief-name {
      text-decoration: none;
      color: $rfx-dark-march;

      &_disabled{
        pointer-events: none;
        opacity: 0.7;
      }

      &:hover {
        text-decoration: underline;
      }
    }

    &__loader {
      display: flex;
      width: 100%;
      justify-content: center;
      height: calc(100vh - 530px);
      align-items: center;
    }

    &__update-loader {
      position: absolute;
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      align-items: center;
      background-color: white;
      top: 0;
      opacity: 0.5;
      z-index: 10000;
    }

    &__update-loader-ring {
      margin-top: 200px;
    }

    &__not-found {
      font-size: 27px;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 60px;
      color: $rfx-medium-grey;
    }

    &__date_wrapper {
      display: flex;
      flex-direction: column;
      width: fit-content;
      max-width: 110px;
    }

    &__date_wrapper-narrow {
      width: 14px;
    }

    &__date-button {
      outline: none;
      border: none;
      text-align: initial;
      font-size: 14px;
      background-color: #fff;
      color: $rfx-dark-march;
      padding-left: 0;
      cursor: pointer;

      &_highlighted {
        color: $rfx-bright-blue;
      }

      &:hover {
        color: $rfx-bright-blue;
      }

      &_icon {
        align-self: auto;
      }

      &_disabled {
        pointer-events: none;
        color: $rfx-light-march;

        &:hover {
          color: $rfx-dark-march;
        }
      }
    }

    &__date {
      height: 0;
    }

    &__select {
      flex-basis: 100%;
    }

  }

  .common-table {
    &__row {
      border: 1px solid $rfx-light-steel;
      border-top: none;
      height: 60px;
      padding: 0 20px;

      &:last-child {
        border-radius: 0 0 6px 6px;
      }
    }

    &__cell {
      border: none;
      padding: 0;

    }

    &__filter-icon {
      color: $rfx-bright-blue;
      margin-left: 10px;
    }

    &__header {
      border: 1px solid $rfx-light-steel;
      border-radius: $rfx-default-border-radius $rfx-default-border-radius 0 0;
      background-color: rgba(1, 22, 39, 0.1);
    }

    &__header-cell {
      color: $rfx-dark-march;
      font-weight: bold;
      border: none;
      cursor: pointer;
      height: 100%;
      padding: 0;
      overflow: visible;

      &:hover {
        background-color: $rfx-light-grey;
      }
    }
  }
</style>
